import React, {useMemo} from 'react'
import {S3Client} from '@aws-sdk/client-s3'
import {Upload} from '@aws-sdk/lib-storage'
import {AssistantApi} from '../services/AssistantApi.service'
import {IResponsePresignedUrl} from '../types/preSigned'
import {captureError} from '../lib/sentry'

const s3 = new S3Client({
    region: 'sa-east-1',
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getErrorDetail = (error: any): string | undefined =>
    error?.response?.data?.detail || error?.response?.data?.message

/**
 * Faz upload de múltiplos arquivos para o Amazon S3.
 * @param files Os arquivos a serem enviados.
 * @param path O caminho (prefixo) onde os arquivos devem ser salvos no S3.
 * @returns Um array com os document_id dos uploads bem-sucedidos.
 */
export const uploadFilesToS3 = async (
    files: File[],
    path: string,
    organization_id: string,
    user_id: string,
    assistant_id: string,
    message_guid?: string
): Promise<string[]> => {
    const api = new AssistantApi()
    const errorContext = {
        context: 'uploadFilesToS3',
        path,
        organization_id,
        user_id,
        assistant_id,
    }

    let results: PromiseSettledResult<IResponsePresignedUrl>[]
    try {
        results = await api.uploadS3PresignedUrls(
            path,
            user_id,
            organization_id,
            files,
            assistant_id,
            message_guid
        )
    } catch (error) {
        captureError(error, {
            ...errorContext,
            fileNames: files.map((file) => file.name),
        })
        throw new Error(
            getErrorDetail(error) || 'Falha ao enviar os arquivos.'
        )
    }

    const documentIds: string[] = []
    let errorMessage = ''

    results.forEach((result, index) => {
        const file = files[index]

        if (result.status === 'fulfilled') {
            documentIds.push(result.value.document_id)
            return
        }

        captureError(result.reason, {...errorContext, fileName: file.name})

        if (!errorMessage) {
            errorMessage =
                getErrorDetail(result.reason) ||
                `Falha ao enviar o arquivo "${file.name}".`
        }
    })

    if (errorMessage) {
        throw new Error(errorMessage)
    }

    return documentIds
}
